import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  FileText,
  Printer,
  Download,
  CheckCircle,
  XCircle,
  Truck,
  Building,
  Package,
  Edit,
  Eye,
  MessageSquare
} from 'lucide-react';
import { useCompra, useUpdateCompra, useGenerateCompraPDF } from '../../hooks/useCompras';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { formatCurrency, formatDate, getFileUrl } from '../../lib/utils';
import { useState } from 'react';
import { Modal } from '../../components/ui/Modal';
import { toast } from 'sonner';
import api from '../../api/axios'; // Asegúrate de que esta ruta apunte a tu instancia de Axios

const getEstadoBadge = (estado: string) => {
  const variants: Record<string, { variant: 'success' | 'danger' | 'warning' | 'info' | 'default'; label: string }> = {
    pendiente: { variant: 'warning', label: 'Pendiente' },
    enviada: { variant: 'info', label: 'Enviada' },
    recibida: { variant: 'success', label: 'Recibida' },
    cancelada: { variant: 'danger', label: 'Cancelada' },
  };
  const config = variants[estado] || variants.pendiente;
  return <Badge variant={config.variant}>{config.label}</Badge>;
};

export function CompraDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const compraId = id ? parseInt(id) : 0;
  const [previewImage, setPreviewImage] = useState<string | null>(null);

  const { data: compra, isLoading, refetch } = useCompra(compraId);
  const updateCompra = useUpdateCompra();
  const generatePDF = useGenerateCompraPDF();

  // Función segura para descargar o visualizar archivos protegidos por JWT
  const handleDownloadFile = async (url: string, fileName: string, openInNewTab = false) => {
    try {
      const toastId = toast.loading('Procesando archivo...');
      const fullUrl = getFileUrl(url);
      
      const response = await api.get(fullUrl, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const localUrl = window.URL.createObjectURL(blob);

      if (openInNewTab) {
        window.open(localUrl, '_blank');
      } else {
        const link = document.createElement('a');
        link.href = localUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
      
      window.URL.revokeObjectURL(localUrl);
      toast.dismiss(toastId);
    } catch (error) {
      toast.dismiss();
      toast.error('Error al acceder al archivo. Verifica tu sesión.');
    }
  };

  const handleChangeEstado = async (nuevoEstado: string) => {
    if (!confirm(`¿Cambiar estado a "${nuevoEstado}"?`)) return;

    try {
      await updateCompra.mutateAsync({
        id: compraId,
        data: { estado: nuevoEstado },
      });
      refetch();
    } catch (error) {
      // Error ya manejado por react-query
    }
  };

  const handleGeneratePDF = async () => {
    try {
      const result = await generatePDF.mutateAsync(compraId);
      if (result?.pdfUrl) {
        toast.success('PDF generado exitosamente');
        refetch();
        handleDownloadFile(result.pdfUrl, `${result.numeroCompra || 'compra'}.pdf`, true);
      }
    } catch (error) {
      toast.error('Error al generar el PDF');
    }
  };

  // Obtener imágenes de la compra
  const compraAny = compra as any;
  const imagenes = compraAny?.imagenes 
    ? (typeof compraAny.imagenes === 'string' 
        ? compraAny.imagenes.split(',').filter((u: string) => u.trim()) 
        : compraAny.imagenes)
    : [];

  const pdfUrl = compraAny?.pdfUrl || null;
  const observaciones = compraAny?.observaciones || null;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Cargando compra...</div>
      </div>
    );
  }

  if (!compra) {
    return (
      <div className="text-center py-8 text-red-500">
        Compra no encontrada
      </div>
    );
  }

  const puedeEditar = compra.estado === 'pendiente';
  const puedeEnviar = compra.estado === 'pendiente';
  const puedeRecibir = compra.estado === 'enviada';
  const puedeCancelar = compra.estado === 'pendiente' || compra.estado === 'enviada';
  const proveedor = compra.proveedor as any;

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/compras')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-gray-800">
                {compra.numeroCompra}
              </h1>
              {getEstadoBadge(compra.estado)}
            </div>
            <p className="text-sm text-gray-500">
              {proveedor?.nombre} • {formatDate(compra.createdAt)}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          {puedeEditar && (
            <Button
              variant="outline"
              onClick={() => navigate(`/compras/editar/${compra.id}`)}
            >
              <Edit size={16} className="mr-2" />
              Editar
            </Button>
          )}
          {puedeEnviar && (
            <Button
              className="bg-blue-600 hover:bg-blue-700 text-white"
              onClick={() => handleChangeEstado('enviada')}
            >
              <Truck size={16} className="mr-2" />
              Marcar como Enviada
            </Button>
          )}
          {puedeRecibir && (
            <Button
              className="bg-green-600 hover:bg-green-700 text-white"
              onClick={() => handleChangeEstado('recibida')}
            >
              <CheckCircle size={16} className="mr-2" />
              Marcar como Recibida
            </Button>
          )}
          {puedeCancelar && (
            <Button
              variant="outline"
              className="text-red-600 border-red-200 hover:bg-red-50"
              onClick={() => handleChangeEstado('cancelada')}
            >
              <XCircle size={16} className="mr-2" />
              Cancelar
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Columna principal */}
        <div className="lg:col-span-2 space-y-6">
          {/* Proveedor */}
          <Card className="p-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <Building size={20} className="text-blue-500" />
              Proveedor
            </h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Nombre</p>
                <p className="font-medium text-gray-800">{proveedor?.nombre}</p>
              </div>
              {proveedor?.telefono && (
                <div>
                  <p className="text-sm text-gray-500">Teléfono</p>
                  <p className="font-medium text-gray-800">{proveedor.telefono}</p>
                </div>
              )}
              {proveedor?.email && (
                <div>
                  <p className="text-sm text-gray-500">Email</p>
                  <p className="font-medium text-gray-800">{proveedor.email}</p>
                </div>
              )}
              {compra.fechaEntrega && (
                <div>
                  <p className="text-sm text-gray-500">Fecha de Entrega</p>
                  <p className="font-medium text-gray-800">
                    {new Date(compra.fechaEntrega).toLocaleString('es-AR', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </div>
              )}
            </div>
          </Card>

          {/* Productos */}
          <Card className="p-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <Package size={20} className="text-blue-500" />
              Productos
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-sm text-gray-500 border-b">
                    <th className="pb-2">Producto</th>
                    <th className="pb-2 text-center">Cantidad</th>
                    <th className="pb-2 text-right">Precio</th>
                    <th className="pb-2 text-right">Subtotal</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {compra.detalles?.map((detalle: any, index: number) => (
                    <tr key={index} className="text-sm">
                      <td className="py-2">
                        <p className="font-medium text-gray-800">{detalle.producto?.nombre || 'Producto'}</p>
                        {detalle.producto?.sku && (
                          <p className="text-xs text-gray-400">SKU: {detalle.producto.sku}</p>
                        )}
                      </td>
                      <td className="py-2 text-center">{detalle.cantidad}</td>
                      <td className="py-2 text-right">{formatCurrency(detalle.precioUnitario)}</td>
                      <td className="py-2 text-right font-medium">{formatCurrency(detalle.subtotal)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t font-bold">
                    <td colSpan={3} className="py-2 text-right">Total</td>
                    <td className="py-2 text-right text-blue-600">{formatCurrency(compra.total)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </Card>

          {/* Archivos - Imágenes y PDF */}
          {(imagenes.length > 0 || pdfUrl) && (
            <Card className="p-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <FileText size={20} className="text-blue-500" />
                Archivos
              </h2>
              
              {/* Imágenes */}
              {imagenes.length > 0 && (
                <div className="mb-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">Imágenes</p>
                  <div className="flex flex-wrap gap-3">
                    {imagenes.map((url: string, index: number) => (
                      <div key={index} className="relative group">
                        <img
                          src={getFileUrl(url)}
                          alt={`Imagen ${index + 1}`}
                          className="w-24 h-24 object-cover rounded-lg border border-gray-200 cursor-pointer hover:opacity-90 transition-opacity"
                          onClick={() => setPreviewImage(getFileUrl(url))}
                        />
                        <button
                          onClick={() => setPreviewImage(getFileUrl(url))}
                          className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center"
                          title="Ver imagen ampliada"
                        >
                          <Eye size={20} className="text-white" />
                        </button>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-gray-400 mt-2">Haz clic en una imagen para ampliarla</p>
                </div>
              )}

              {/* PDF adjunto */}
              {pdfUrl && (
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">PDF</p>
                  <div className="flex items-center gap-3 bg-blue-50 px-4 py-3 rounded-lg border border-blue-200">
                    <FileText size={24} className="text-red-500" />
                    <button
                      onClick={() => handleDownloadFile(pdfUrl, pdfUrl.split('/').pop() || 'documento.pdf', true)}
                      className="text-blue-600 hover:underline font-medium flex-1 text-left"
                    >
                      {pdfUrl.split('/').pop() || 'Documento PDF'}
                    </button>
                    <button
                      onClick={() => handleDownloadFile(pdfUrl, pdfUrl.split('/').pop() || 'documento.pdf', false)}
                      className="p-2 text-green-600 hover:bg-green-100 rounded-lg transition-colors"
                      title="Descargar PDF"
                    >
                      <Download size={18} />
                    </button>
                  </div>
                </div>
              )}
            </Card>
          )}

          {/* Observaciones */}
          {observaciones && (
            <Card className="p-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <MessageSquare size={20} className="text-blue-500" />
                Observaciones
              </h2>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <p className="text-gray-700 whitespace-pre-wrap">{observaciones}</p>
              </div>
            </Card>
          )}
        </div>

        {/* Columna derecha */}
        <div className="space-y-6">
          {/* Resumen */}
          <Card className="p-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Resumen</h2>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Subtotal</span>
                <span>{formatCurrency(compra.subtotal)}</span>
              </div>
              {compra.costoEnvio > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Costo de Envío</span>
                  <span>{formatCurrency(compra.costoEnvio)}</span>
                </div>
              )}
              {compra.descuento > 0 && (
                <div className="flex justify-between text-sm text-green-600">
                  <span>Descuento</span>
                  <span>-{formatCurrency(compra.descuento)}</span>
                </div>
              )}
              <div className="border-t pt-2 mt-2">
                <div className="flex justify-between font-bold text-lg">
                  <span>Total</span>
                  <span className="text-blue-600">{formatCurrency(compra.total)}</span>
                </div>
              </div>
            </div>
          </Card>

          {/* Historial */}
          <Card className="p-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Historial</h2>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Creado</span>
                <span className="text-gray-700">{formatDate(compra.createdAt)}</span>
              </div>
              {compra.fechaRecepcion && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Recibido</span>
                  <span className="text-gray-700">{formatDate(compra.fechaRecepcion)}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-gray-500">Usuario</span>
                <span className="text-gray-700">{compra.usuarioCreador || 'Sistema'}</span>
              </div>
            </div>
          </Card>

          {/* PDF del sistema */}
          {compra.pdfUrl ? (
            <Card className="p-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">PDF del Sistema</h2>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1 text-blue-600 border-blue-200 hover:bg-blue-50"
                  onClick={() => handleDownloadFile(compra.pdfUrl as string, `${compra.numeroCompra}.pdf`, true)}
                >
                  <FileText size={16} className="mr-2" />
                  Ver
                </Button>
                <Button
                  variant="outline"
                  className="flex-1 text-green-600 border-green-200 hover:bg-green-50"
                  onClick={() => handleDownloadFile(compra.pdfUrl as string, `${compra.numeroCompra}.pdf`, false)}
                >
                  <Download size={16} className="mr-2" />
                  Descargar
                </Button>
              </div>
            </Card>
          ) : (
            <Card className="p-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">PDF del Sistema</h2>
              <Button 
                onClick={handleGeneratePDF} 
                className="w-full" 
                variant="outline"
                disabled={generatePDF.isPending}
              >
                <Printer size={16} className="mr-2" />
                {generatePDF.isPending ? 'Generando...' : 'Generar PDF'}
              </Button>
            </Card>
          )}
        </div>
      </div>

      {/* Vista previa de imágenes */}
      <Modal
        isOpen={!!previewImage}
        onClose={() => setPreviewImage(null)}
        title="Vista previa de imagen"
        size="lg"
      >
        <div className="flex items-center justify-center min-h-[300px]">
          {previewImage && (
            <img
              src={previewImage}
              alt="Vista previa"
              className="max-w-full max-h-[70vh] object-contain rounded-lg"
            />
          )}
        </div>
        <div className="mt-4 text-center">
          <button
            onClick={() => {
              if (previewImage) {
                window.open(previewImage, '_blank');
              }
            }}
            className="text-sm text-blue-600 hover:underline"
          >
            Abrir imagen en nueva pestaña
          </button>
        </div>
      </Modal>
    </div>
  );
}